import { useCallback } from "react";

import { createRouteHelpers } from "../../shared/utils/createRouteHelpers";
import { useUrlQueryFilters } from "../../utils/router";
import { ImportBatchFilter } from "./components/importBatchFilters/types";
import { ImportBatchesFilters } from "./types";

export const BULK_IMPORTS_ROOT_PATH = "/member/imports" as const;

export const PARAMETER_BATCH_ID = "batchId";

// payables (v3 — /v3/platform/batches, type PayableImport)

export const PATH_IMPORT_PAYABLES =
  `${BULK_IMPORTS_ROOT_PATH}/payables` as const;
export const PATH_IMPORT_PAYABLES_START =
  `${PATH_IMPORT_PAYABLES}/start` as const;
export const PATH_IMPORT_PAYABLES_BATCH =
  `${PATH_IMPORT_PAYABLES}/:${PARAMETER_BATCH_ID}` as const;
export const PATH_IMPORT_PAYABLES_BATCH_HISTORY =
  `${PATH_IMPORT_PAYABLES_BATCH}/history` as const;

export const routeHelpersImportPayables = createRouteHelpers<
  never,
  { search: { filter?: ImportBatchFilter; year?: string } }
>(PATH_IMPORT_PAYABLES);

/**
 * Back to the payables list on the year the toolbar is filtered by. Leaving the
 * year behind would drop the list back to every year while the select still
 * reads the one that was picked.
 */
export const useGoToImportPayablesBatches = () => {
  const goToImportPayables = routeHelpersImportPayables.useGoTo();
  const { filters } = useUrlQueryFilters<ImportBatchesFilters>({});

  return useCallback(
    () => goToImportPayables({ search: { year: filters.year } }),
    [goToImportPayables, filters.year]
  );
};

export const routeHelpersImportPayablesStart = createRouteHelpers(
  PATH_IMPORT_PAYABLES_START
);

export const routeHelpersImportPayablesBatch = createRouteHelpers<
  { batchId: string },
  { state: { showBatchCreatedModal?: boolean } }
>(PATH_IMPORT_PAYABLES_BATCH);

export const routeHelpersImportPayablesBatchHistory = createRouteHelpers<{
  batchId: string;
}>(PATH_IMPORT_PAYABLES_BATCH_HISTORY);

// contractors (v3 — /v3/platform/batches, type PayeeImport)

/**
 * The URL segment stays `collaborators` even though everything user-facing says
 * "contractors": it is the address customers have bookmarked, and renaming it
 * is not part of moving the screen onto v3.
 */
export const PATH_IMPORT_CONTRACTORS =
  `${BULK_IMPORTS_ROOT_PATH}/collaborators` as const;
export const PATH_IMPORT_CONTRACTORS_START =
  `${PATH_IMPORT_CONTRACTORS}/start` as const;
export const PATH_IMPORT_CONTRACTORS_BATCH =
  `${PATH_IMPORT_CONTRACTORS}/:${PARAMETER_BATCH_ID}` as const;
export const PATH_IMPORT_CONTRACTORS_BATCH_HISTORY =
  `${PATH_IMPORT_CONTRACTORS_BATCH}/history` as const;

export const routeHelpersImportContractors = createRouteHelpers<
  never,
  { search: { filter?: ImportBatchFilter; year?: string } }
>(PATH_IMPORT_CONTRACTORS);

/** Contractor twin of {@link useGoToImportPayablesBatches}. */
export const useGoToImportContractorsBatches = () => {
  const goToImportContractors = routeHelpersImportContractors.useGoTo();
  const { filters } = useUrlQueryFilters<ImportBatchesFilters>({});

  return useCallback(
    () => goToImportContractors({ search: { year: filters.year } }),
    [goToImportContractors, filters.year]
  );
};

export const routeHelpersImportContractorsStart = createRouteHelpers(
  PATH_IMPORT_CONTRACTORS_START
);

export const routeHelpersImportContractorsBatch = createRouteHelpers<
  { batchId: string },
  { state: { showBatchCreatedModal?: boolean } }
>(PATH_IMPORT_CONTRACTORS_BATCH);

export const routeHelpersImportContractorsBatchHistory = createRouteHelpers<{
  batchId: string;
}>(PATH_IMPORT_CONTRACTORS_BATCH_HISTORY);
